import React, { useState } from 'react';
import { PageRow } from '@/components/layout/page-layout/general-layout/PageLayout';
import { useRightSidebarConfig } from '@/components/layout/side-bars/right-context-sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface RowSettingsSidebarProps {
  row: PageRow;
  rowIndex: number;
  onRowUpdate: (rowId: string, updates: Partial<PageRow>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export default function RowSettingsSidebar({
  row,
  rowIndex,
  onRowUpdate,
  onRemove,
  canRemove
}: RowSettingsSidebarProps) {
  const { t } = usePlatformTranslation();
  const [isDirty, setIsDirty] = useState(false);
  const [localRow, setLocalRow] = useState<PageRow>(row);
  const { close } = useRightSidebarConfig();

  const handleRowChange = (updates: Partial<PageRow>) => {
    const newRow = { ...localRow, ...updates };
    setLocalRow(newRow);
    setIsDirty(true);
  };

  const handleSave = () => {
    onRowUpdate(row.id, localRow);
    setIsDirty(false);
  };

  const handleReset = () => {
    setLocalRow(row);
    setIsDirty(false);
  };

  const handleRemove = () => {
    onRemove();
    close(); // Close the sidebar after removing the row
  };

  return (
    <div className="space-y-6">
      {/* Row Information */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">{t('platform.layout.page-layout.row_information')}</h3>
        
        <div className="space-y-2">
          <Label htmlFor="rowTitle">{t('platform.layout.page-layout.row_title')}</Label>
          <Input
            id="rowTitle"
            value={localRow.title || `Row ${rowIndex + 1}`}
            onChange={(e) => handleRowChange({ title: e.target.value })}
            placeholder={t('platform.layout.page-layout.enter_row_title')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="backgroundColor">{t('platform.layout.page-layout.background_color')}</Label>
          <div className="flex items-center space-x-2">
            <Input
              id="backgroundColor"
              type="color"
              value={localRow.backgroundColor || '#ffffff'}
              onChange={(e) => handleRowChange({ backgroundColor: e.target.value })}
              className="w-16 h-10 p-1 border rounded"
            />
            <Input
              type="text"
              value={localRow.backgroundColor || '#ffffff'}
              onChange={(e) => handleRowChange({ backgroundColor: e.target.value })}
              className="flex-1"
              placeholder="#ffffff"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Layout Configuration */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">{t('platform.layout.page-layout.layout_configuration')}</h3>
        
        <div className="space-y-2">
          <Label htmlFor="gap">{t('platform.layout.page-layout.gap_between_elements')}</Label>
          <Input
            id="gap"
            type="number"
            value={localRow.layoutConfig.gap || 40}
            onChange={(e) => handleRowChange({
              layoutConfig: {
                ...localRow.layoutConfig,
                gap: parseInt(e.target.value) || 40
              }
            })}
            min="0"
            max="100"
            step="5"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="padding">{t('platform.layout.page-layout.padding')}</Label>
          <Input
            id="padding"
            type="number"
            value={localRow.layoutConfig.padding || 0}
            onChange={(e) => handleRowChange({
              layoutConfig: {
                ...localRow.layoutConfig,
                padding: parseInt(e.target.value) || 0
              }
            })}
            min="0"
            max="100"
            step="5"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="totalSlots">{t('platform.layout.page-layout.number_of_slots')}</Label>
          <Input
            id="totalSlots"
            type="number"
            value={localRow.layoutConfig.totalSlots || Math.max(localRow.layoutConfig.elements.length, 1)}
            onChange={(e) => handleRowChange({
              layoutConfig: {
                ...localRow.layoutConfig,
                totalSlots: parseInt(e.target.value) || 1
              }
            })}
            min="1"
            max="10"
          />
        </div>
      </div>

      <Separator />

      {/* Actions */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={!isDirty}
            className="flex-1"
          >
            Save Changes
          </Button>
          <Button
            onClick={handleReset}
            disabled={!isDirty}
            variant="outline"
            className="flex-1"
          >
            Reset
          </Button>
        </div>
        
        {canRemove && (
          <Button
            onClick={handleRemove}
            variant="destructive"
            className="w-full"
          >
            Remove Row
          </Button>
        )}
      </div>
    </div>
  );
} 